import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import useStreamManager from './useStreamManager';

export const useHLSVideo = (hlsUrl, cameraId = null) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [isHLSLoading, setIsHLSLoading] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [viewerCount, setViewerCount] = useState(0);
  
  // Stream manager for sharing integration
  const streamManager = useStreamManager();

  // Get absolute HLS URL
  const getAbsoluteHlsUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;

    const baseUrl =
      import.meta.env.VITE_API_STREAM_URL ||
      import.meta.env.VITE_API_URL?.replace("/api", "") ||
      "http://localhost:3667";

    return baseUrl + url;
  };

  // Initialize HLS.js for video playback
  const initializeHLS = (videoElement, hlsUrl) => {
    if (!videoElement || !hlsUrl) {
      console.error('HLS initialization failed:', { videoElement: !!videoElement, hlsUrl });
      return;
    }

    console.log('Starting HLS initialization:', { hlsUrl, videoElementReady: videoElement.readyState });

    // Clean up any existing HLS instance
    if (hlsRef.current) {
      console.log('Destroying existing HLS instance');
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Reset video element
    videoElement.src = '';
    videoElement.load();

    if (Hls.isSupported()) {
      console.log('HLS.js is supported, creating new instance');
      const hls = new Hls({
        debug: true, // Enable debug for troubleshooting
        enableWorker: true,
        lowLatencyMode: true,
        liveBackBufferLength: 10, // Keep 10 seconds of buffer
        liveSyncDurationCount: 3, // Live sync duration
        liveMaxLatencyDurationCount: 10, // Max latency count
        maxBufferLength: 30, // Maximum buffer length
        maxMaxBufferLength: 60, // Maximum max buffer length
        maxBufferSize: 60 * 1000 * 1000, // 60MB max buffer size
        maxBufferHole: 0.1, // Max buffer hole size
        highBufferWatchdogPeriod: 1, // High buffer watchdog period
        nudgeOffset: 0.1, // Nudge offset
        nudgeMaxRetry: 3, // Max nudge retry
        maxFragLookUpTolerance: 0.25, // Max fragment lookup tolerance
        liveDurationInfinity: false, // Disable infinity duration for live
        liveBackBufferLength: 10, // Keep last 10 seconds
      });

      hlsRef.current = hls;

      hls.loadSource(hlsUrl);
      hls.attachMedia(videoElement);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('HLS manifest parsed successfully');
        
        // For live streams, seek to the live edge to avoid old content
        if (hls.levels && hls.levels.length > 0 && videoElement.duration === Infinity) {
          console.log('Live stream detected, seeking to live edge');
          videoElement.currentTime = videoElement.duration;
        }
        
        videoElement.play().catch((error) => {
          console.error("Auto-play failed:", error);
        });
        
        // Register HLS stream with sharing integration if cameraId is provided
        if (cameraId) {
          streamManager.registerStream(cameraId, {
            url: hlsUrl,
            type: 'hls',
            userId: 'current_user' // TODO: Get from auth context
          });
          
          // Update viewer count
          const currentViewerCount = streamManager.getViewerCount(cameraId);
          setViewerCount(currentViewerCount);
        }
      });

      // Add event listener to keep video at live edge
      videoElement.addEventListener('loadedmetadata', () => {
        if (videoElement.duration === Infinity) {
          console.log('Setting live stream to current time');
          videoElement.currentTime = videoElement.duration;
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error("HLS error:", data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('Network error, attempting to recover...');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('Media error, attempting to recover...');
              hls.recoverMediaError();
              break;
            default:
              // For other fatal errors, recreate HLS instance
              console.log('Fatal error, recreating HLS instance...');
              setTimeout(() => {
                if (hlsRef.current) {
                  hlsRef.current.destroy();
                  const newHls = new Hls({
                    debug: true,
                    enableWorker: true,
                    lowLatencyMode: true,
                    liveBackBufferLength: 10, // Keep 10 seconds of buffer
                    liveSyncDurationCount: 3, // Live sync duration
                    liveMaxLatencyDurationCount: 10, // Max latency count
                    maxBufferLength: 30, // Maximum buffer length
                    maxMaxBufferLength: 60, // Maximum max buffer length
                    maxBufferSize: 60 * 1000 * 1000, // 60MB max buffer size
                    maxBufferHole: 0.1, // Max buffer hole size
                    highBufferWatchdogPeriod: 1, // High buffer watchdog period
                    nudgeOffset: 0.1, // Nudge offset
                    nudgeMaxRetry: 3, // Max nudge retry
                    maxFragLookUpTolerance: 0.25, // Max fragment lookup tolerance
                    liveDurationInfinity: false, // Disable infinity duration for live
                    liveBackBufferLength: 10, // Keep last 10 seconds
                  });
                  hlsRef.current = newHls;
                  newHls.loadSource(hlsUrl);
                  newHls.attachMedia(videoElement);
                }
              }, 3000);
              break;
          }
        }
      });
    } else if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
      console.log('Using native HLS support');
      videoElement.src = hlsUrl;
      videoElement.addEventListener("loadedmetadata", () => {
        console.log('Native HLS metadata loaded');
        videoElement.play().catch((error) => {
          console.error("Auto-play failed:", error);
        });
      });
    } else {
      console.error('HLS not supported in this browser');
    }
  };

  // Initialize HLS when hlsUrl changes
  useEffect(() => {
    console.log('🎯 useHLSVideo: hlsUrl changed to:', hlsUrl);
    console.log('🎯 useHLSVideo: videoRef.current exists:', !!videoRef.current);
    
    if (hlsUrl && videoRef.current) {
      const hlsUrlAbsolute = getAbsoluteHlsUrl(hlsUrl);
      console.log('🎯 useHLSVideo: Initializing HLS with URL:', hlsUrlAbsolute);
      
      // Cleanup any existing HLS instance
      if (hlsRef.current) {
        console.log('Destroying existing HLS instance');
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      
      // Check if the HLS file is available before initializing
      let retryCount = 0;
      const maxRetries = 30; // 30 seconds timeout
      let isActive = true;
      
              const checkFileAvailability = () => {
          if (!isActive || retryCount >= maxRetries) {
            if (retryCount >= maxRetries) {
              console.error('HLS file not available after 30 seconds, giving up');
              setIsHLSLoading(false);
            }
            return;
          }
        
        setIsHLSLoading(true);
        setRetryAttempt(retryCount);
        
        fetch(hlsUrlAbsolute, { method: 'HEAD' })
          .then(response => {
            if (response.ok) {
              console.log('HLS file is available, initializing...');
              setIsHLSLoading(false);
              setRetryAttempt(0);
              // Initialize immediately if video element is ready
              if (videoRef.current.readyState >= 0) {
                initializeHLS(videoRef.current, hlsUrlAbsolute);
              } else {
                // Wait for video element to be ready
                const checkVideoReady = () => {
                  if (videoRef.current && videoRef.current.readyState >= 0) {
                    initializeHLS(videoRef.current, hlsUrlAbsolute);
                  } else {
                    setTimeout(checkVideoReady, 100);
                  }
                };
                checkVideoReady();
              }
            } else {
              retryCount++;
              console.log(`HLS file not ready yet, retrying in 1 second... (attempt ${retryCount}/${maxRetries})`);
              setRetryAttempt(retryCount);
              setTimeout(checkFileAvailability, 1000);
            }
          })
          .catch(error => {
            retryCount++;
            console.log(`Error checking HLS file availability, retrying in 1 second... (attempt ${retryCount}/${maxRetries})`, error);
            setRetryAttempt(retryCount);
            setTimeout(checkFileAvailability, 1000);
          });
      };
      
      // Start checking for file availability
      const startCheck = () => {
        if (!isActive) return;
        checkFileAvailability();
      };
      startCheck();
      
      // Cleanup function to stop checking if URL changes
      return () => {
        isActive = false;
        setIsHLSLoading(false);
        setRetryAttempt(0);
      };
    }

    // Cleanup function
    return () => {
      if (hlsRef.current) {
        console.log('Cleaning up HLS instance');
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [hlsUrl]);

  // Cleanup HLS on unmount
  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, []);

  const retryHLS = () => {
    if (hlsUrl && videoRef.current) {
      const hlsUrlAbsolute = getAbsoluteHlsUrl(hlsUrl);
      initializeHLS(videoRef.current, hlsUrlAbsolute);
    }
  };

  return {
    videoRef,
    retryHLS,
    isHLSLoading,
    retryAttempt,
    viewerCount,
    getViewerCount: () => cameraId ? streamManager.getViewerCount(cameraId) : 0,
  };
};
